import * as THREE from "three";
import { BaseModify } from "../base/BaseModify";
import { Context } from "../core/context";
import { DBEntity, DbLine, DbMesh, FMathUtils, ViewManager } from "@/CadEngine";
import {
  DrawOperationMovePointBeforeParams,
  DrawOperationMovePointParams,
  IModifyOperation,
  ModifyOperateState,
} from "./IModifyOperation";
import { generateUUID } from "three/src/math/MathUtils";
import { LineDimension } from "@/Dimensions/LineDimension";
import { ArcDimension } from "@/Dimensions/ArcDimension";
import { AnchorPointAux } from "@/Dimensions/AnchorPointAuxs";
import { OrthogonalAux } from "@/Dimensions/OrthogonalAux";
import { CircleAnchorPointAuxs } from "@/Dimensions/CircleAnchorPointAuxs";
import { IndsertPosition } from "@/core/IndserObject";
import { ActiveInputType, CommonColor } from "@/core/Parameters";
import { DimensionGroup } from "@/core/DimensionGroup";

class MirrorModify implements IModifyOperation {
  private m_context: Context;
  private m_selects: DBEntity[] = [];
  private m_points: THREE.Vector3[] = [];
  private m_curPoint: THREE.Vector3 = new THREE.Vector3();
  private m_auxLine: THREE.Line | undefined;
  private copySelects: {
    lines: DbLine[];
    meshs: DbMesh[];
  } = {
    lines: [],
    meshs: [],
  };
  private mirrorMatrix: THREE.Matrix4 = new THREE.Matrix4();
  public get Points() {
    return this.m_points;
  }
  private m_dimension: DimensionGroup;
  private m_locationObject: THREE.Line;
  private m_originExtendIds: string[] = [];
  private m_returnData: any = {
    fullCopyDatas: null,
  };
  private m_editLines: DbLine[] = [];
  private m_editMeshs: DbMesh[] = [];
  private m_copyDatas: (DbLine | DbMesh)[] = [];
  private m_tempMoveObjectGroup: THREE.Group = new THREE.Group();
  private m_curVector: THREE.Vector3 = new THREE.Vector3();
  private m_activeInputType: ActiveInputType = ActiveInputType.None;
  private m_inputValue: string = "";
  private m_distanceInputValue: string = "";
  private m_angleInputValue: string = "";
  private m_blockIdAndRootIds: string[] = [];
  private m_copyDataMatrix: Map<string, THREE.Matrix4> = new Map();
  private m_curAngle: number = 0;
  constructor(context: Context) {
    this.m_context = context;
    this.m_dimension = new DimensionGroup(
      context.ViewManager,
      context.SceneManager.AuxModel
    );
    this.m_locationObject = context.ToolsManager.CreatedLocationLine();
    context.SceneManager.AuxModel.add(this.m_tempMoveObjectGroup);
    this.m_dimension.Line.SetVaule("");
    this.m_dimension.Arc.SetVaule("");
    this.m_dimension.Line.Input.Dom.style.border = "1px solid #fff";
    this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
    this.m_selects =
      this.m_context.SelectionManager.GetSelected() as DBEntity[];
    this.m_originExtendIds = this.m_context.ToolsManager.getExtendIds(
      this.m_selects as DbLine[]
    );
    let { editLines, editMeshs } = this.m_context.ToolsManager.GetSameTypeData(
      this.m_selects
    );
    this.m_editLines = editLines;
    this.m_editMeshs = editMeshs;
    this.createTempMoveModel();
    this.m_inputValue = "";
    this.m_blockIdAndRootIds = this.m_context.ToolsManager.getBlockIdAndRootIds(
      this.m_selects as DbLine[]
    );
  }

  OnMovePointBefore(params: DrawOperationMovePointBeforeParams): void {}
  OnMovePoint(point: DrawOperationMovePointParams): void {
    this.m_curPoint = point.point.clone();
    this.m_locationObject.position.copy(this.m_curPoint);
    if (this.m_points.length) {
      this.m_auxLine?.geometry.setFromPoints([
        this.m_points[0],
        this.m_curPoint,
      ]);
      this.m_auxLine?.computeLineDistances();
      this.m_curVector = this.m_curPoint.clone().sub(this.m_points[0]);
      this.updateDimension();
      this.updateSelectedModelsPosition();
      this.m_activeInputType = ActiveInputType.None;
      this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
      this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
      this.m_dimension.Arc.Label.Visibility = false;
      this.m_inputValue = "";
    }
  }
  OnDownPoint(point: DrawOperationMovePointParams): void {
    if (this.m_points.length == 0) {
      this.m_auxLine = this.m_context.ToolsManager.CreatedAuxLine();
      let selects: DBEntity[] = [];
      for (let i = 0; i < this.m_selects.length; i++) {
        if (this.m_selects[i].root_reference_id) {
          let rootId = this.m_selects[i].root_reference_id;
          selects.push(
            ...this.m_context.ModelManager.GetRootReferenceIdDatas(rootId!)
          );
        }
      }
      this.m_selects.push(...selects);
      this.m_selects = [...new Set(this.m_selects)];
      this.createCopyData();
    }
    this.m_points.push(point.point);
    if (this.m_points.length >= 2) {
      this.m_context.ModifyManager.ModifyFinish(0);
    }
  }
  onKeyDown(e: KeyboardEvent): void {
    if (this.m_points.length >= 1) {
      if (e.key === "Tab") {
        this.m_inputValue = "";
        if (this.m_activeInputType == ActiveInputType.None) {
          this.m_activeInputType = ActiveInputType.Distance;
          this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
          this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
        } else if (this.m_activeInputType == ActiveInputType.Distance) {
          this.m_activeInputType = ActiveInputType.Angle;
          this.m_dimension.Line.Input.Dom.style.border = "1px solid #101010";
          this.m_dimension.Arc.Input.Dom.style.border = "1px solid #ffffff";
        } else if (this.m_activeInputType == ActiveInputType.Angle) {
          this.m_activeInputType = ActiveInputType.Distance;
          this.m_dimension.Line.Input.Dom.style.border = "1px solid #ffffff";
          this.m_dimension.Arc.Input.Dom.style.border = "1px solid #101010";
        }
      }
      if (e.key === "Backspace") {
        if (this.m_inputValue.length > 0) {
          this.m_inputValue = this.m_inputValue.substring(
            0,
            this.m_inputValue.length - 1
          );
        }
        if (this.m_activeInputType == ActiveInputType.Distance) {
          this.m_dimension.Line.Input.SetVaule(this.m_inputValue + " mm");
          if (this.m_inputValue) {
            this.m_distanceInputValue = this.m_inputValue;
          }
        } else if (this.m_activeInputType == ActiveInputType.Angle) {
          this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
          if (this.m_inputValue) {
            this.m_angleInputValue = this.m_inputValue;
          }
        }
      } else if ((e.key >= "0" && e.key <= "9") || e.key == ".") {
        if (this.m_activeInputType == ActiveInputType.None) return;
        if (this.m_inputValue?.includes(".") && e.key == ".") return;
        this.m_inputValue += e.key;
        if (this.m_activeInputType == ActiveInputType.Distance) {
          this.m_dimension.Line.Input.SetVaule(this.m_inputValue + " mm");
          this.m_distanceInputValue = this.m_inputValue;
        } else if (this.m_activeInputType == ActiveInputType.Angle) {
          this.m_dimension.Arc.Input.SetVaule(this.m_inputValue + " °");
          this.m_angleInputValue = this.m_inputValue;
        }
      } else if (e.key == "Enter") {
        let length = parseFloat(this.m_distanceInputValue) * 0.001;
        let angle = THREE.MathUtils.degToRad(
          parseFloat(this.m_angleInputValue)
        );
        let dir = new THREE.Vector3(1, 0, 0).normalize();
        dir.applyAxisAngle(new THREE.Vector3(0, 0, 1), angle);
        dir.multiplyScalar(length);
        this.m_curVector = dir.clone();
        let newVector = new THREE.Vector3();
        newVector.addVectors(this.m_points[0], dir);
        this.m_curPoint = newVector.clone();
        this.m_auxLine?.geometry.setFromPoints([
          this.m_points[0],
          this.m_curPoint,
        ]);
        this.m_auxLine?.computeLineDistances();
        this.updateSelectedModelsPosition();
        this.m_context.ViewManager.Render();
        this.m_context.ModifyManager.ModifyFinish(0);
      }
    }
  }
  onKeyUp(e: KeyboardEvent): void {}

  OnZoom(camera: THREE.Camera): void {}

  Finish(operate: ModifyOperateState) {
    this.clear();

    if (operate == ModifyOperateState.complete) {
      this.endHandle();

      this.m_returnData.matrix = this.mirrorMatrix;
      return this.m_returnData;
    } else if (operate == ModifyOperateState.cancel) {
      return [];
    }
  }
  OnWheel(e: WheelEvent): void {}

  onMouseMove(e: PointerEvent): void {}

  onMouseDown(e: PointerEvent): void {}

  onMouseUp(e: PointerEvent): void {
    if (e.button !== 0) return;
    this.m_context.SelectionManager.SetSelected(...this.m_selects);
  }

  private get scale() {
    return this.m_context.ViewManager.ScreenModelScale;
  }
  private get curDir() {
    return this.m_curPoint.clone().sub(this.m_points[0]);
  }
  private updateDimension() {
    let lineInputPosition = new THREE.Vector3();
    lineInputPosition.x = this.m_curPoint.x + this.scale * 45;
    lineInputPosition.y = this.m_curPoint.y - this.scale;

    let distance = (this.m_curPoint.distanceTo(this.m_points[0]) * 1000)
      .toFixed(2)
      .toString();
    this.m_dimension.Line.Input.SetVaule(
      distance + " mm",
      lineInputPosition,
      new THREE.Vector3(1, 0, 0)
    );
    this.m_distanceInputValue = distance;

    let arcInputPosition = new THREE.Vector3();
    arcInputPosition.x = this.m_curPoint.x + this.scale * 120;
    arcInputPosition.y = this.m_curPoint.y - this.scale;
    let xAxis = new THREE.Vector3(1, 0, 0);
    let angle: string | number = xAxis.angleTo(
      this.m_curVector.clone().normalize()
    );
    if (this.m_curVector.clone().normalize().clone().cross(xAxis).z > 0) {
      angle = Math.PI * 2 - angle;
    }
    angle = ((angle * 180) / Math.PI).toFixed(2).toString();
    this.m_curAngle = Number(angle);
    this.m_dimension.Arc.Input.SetVaule(
      angle + " °",
      arcInputPosition,
      new THREE.Vector3(1, 0, 0)
    );
    this.m_angleInputValue = angle;
  }
  private updateSelectedModelsPosition() {
    let Tmatrix = new THREE.Matrix4();
    Tmatrix.makeTranslation(
      this.m_points[0].x,
      this.m_points[0].y,
      this.m_points[0].z
    );
    let zAxis = new THREE.Vector3(0, 0, 1);
    let ABV = this.m_curPoint.clone().sub(this.m_points[0]).normalize();
    let unitDeltV = zAxis.cross(ABV).normalize();
    let m = new THREE.Matrix4();
    m.set(
      1 - 2 * unitDeltV.x * unitDeltV.x,
      -2 * unitDeltV.x * unitDeltV.y,
      -2 * unitDeltV.x * unitDeltV.z,
      0,
      -2 * unitDeltV.x * unitDeltV.y,
      1 - 2 * unitDeltV.y * unitDeltV.y,
      -2 * unitDeltV.y * unitDeltV.z,
      0,
      -2 * unitDeltV.x * unitDeltV.z,
      -2 * unitDeltV.y * unitDeltV.z,
      1 - 2 * unitDeltV.z * unitDeltV.z,
      0,
      0,
      0,
      0,
      1
    );
    m.transpose();
    // let p=new THREE.Vector3()
    // let q=new THREE.Quaternion()
    // let s=new THREE.Vector3()
    // m.decompose(p,q,s)
    // let angle=new THREE.Euler().setFromQuaternion(q).z
    // console.log(angle,'angle000')
    let composeM = Tmatrix.clone()
      .multiply(m)
      .multiply(Tmatrix.clone().invert()); //反射矩阵

    this.mirrorMatrix = composeM;
    this.updateTempMoveModel();
  }

  private createCopyData() {
    let copyDatas: any[] = [];
    for (let i = 0; i < this.m_originExtendIds.length; i++) {
      copyDatas.push({
        originExtendId: this.m_originExtendIds[i],
        copyExtendId: generateUUID(),
      });
    }
    for (let i = 0; i < this.m_tempMoveObjectGroup.children.length; i++) {
      let child = this.m_tempMoveObjectGroup.children[i];
      let copyData = undefined;

      if (child instanceof THREE.LineSegments) {
        copyData = DbLine.clone(child.userData.originData);
      } else if (child instanceof THREE.Mesh) {
        copyData = DbMesh.clone(child.userData.originData);
      }
      if (copyData) {
        copyData.id = generateUUID();
        copyData.block_path = generateUUID();
        for (let j = 0; j < copyDatas.length; j++) {
          if (copyData.extend_id == copyDatas[j].originExtendId) {
            copyData.extend_id = copyDatas[j].copyExtendId;
            if (copyData.root_reference_id) {
              let blockIdAndRootId =
                copyData.blockId + ":" + copyData.root_reference_id;
              if (this.m_blockIdAndRootIds.includes(blockIdAndRootId)) {
                let matrix =
                  this.m_context.ToolsManager.passBlockIdAndRootIdGetMatrix(
                    blockIdAndRootId
                  )?.clone();
                copyData.root_reference_id = copyDatas[j].copyExtendId;
                if (matrix) {
                  let copyBlockIdAndRootId =
                    copyData.blockId + ":" + copyData.root_reference_id;
                  if (!this.m_copyDataMatrix.has(copyBlockIdAndRootId)) {
                    this.m_copyDataMatrix.set(copyBlockIdAndRootId, matrix);
                  }
                }
              }
            }
          }
        }
        child.userData.copyData = copyData;
        this.m_copyDatas.push(copyData);
      }
    }
    this.m_returnData.copyDatas = copyDatas;
  }

  private createTempMoveModel() {
    for (let i = 0; i < this.m_editMeshs.length; i++) {
      let tempObj = this.m_context.ToolsManager.PassDbEntityCreateTempModel(
        this.m_editMeshs[i]
      );
      if (tempObj) {
        this.m_tempMoveObjectGroup.add(tempObj);
      }
    }
    for (let i = 0; i < this.m_editLines.length; i++) {
      let tempObj = this.m_context.ToolsManager.PassDbEntityCreateTempModel(
        this.m_editLines[i]
      );
      if (tempObj) {
        this.m_tempMoveObjectGroup.add(tempObj);
      }
    }
  }
  private updateTempMoveModel() {
    this.m_tempMoveObjectGroup.children.forEach((child) => {
      let ps: number[] = [];
      if (child instanceof THREE.LineSegments || child instanceof THREE.Mesh) {
        if (child instanceof THREE.LineSegments) {
          ps = Array.from(child.userData.originData.points);
        } else if (child instanceof THREE.Mesh) {
          ps = Array.from(child.userData.originData.vertexs);
        }
        for (let i = 0; i < ps.length; i += 3) {
          let vertex = new THREE.Vector3(ps[i], ps[i + 1], ps[i + 2]);
          vertex.applyMatrix4(this.mirrorMatrix);
          ps[i] = vertex.x;
          ps[i + 1] = vertex.y;
          ps[i + 2] = vertex.z;
        }
        child.geometry.setAttribute(
          "position",
          new THREE.BufferAttribute(new Float32Array(ps), 3)
        );
        child.geometry.attributes.position.needsUpdate = true;
        child.geometry.computeBoundingBox();
        child.geometry.computeBoundingSphere();
        if (child instanceof THREE.LineSegments) {
          child.userData.copyData.points =
            child.geometry.attributes.position.array;
        } else if (child instanceof THREE.Mesh) {
          child.userData.copyData.vertexs =
            child.geometry.attributes.position.array;
        }
        child.userData.copyData.box = child.userData.copyData.getBoundingBox();
        child.userData.copyData.sphere =
          child.userData.copyData.getBoundingSphere();
      }
    });
  }

  private endHandle() {
    for (let i = 0; i < this.m_copyDatas.length; i++) {
      let copyData = this.m_copyDatas[i];
      this.m_context.ModelManager.SetReferences(copyData);
      let copyBlockIdAndRootId =
        copyData.blockId + ":" + copyData.root_reference_id;
      if (this.m_copyDataMatrix.has(copyBlockIdAndRootId)) {
        let matrix = this.m_copyDataMatrix.get(copyBlockIdAndRootId)!;
        matrix.multiply(this.mirrorMatrix);
        this.m_context.ModelManager.AddBlockInstance(copyData, matrix);
        this.m_copyDataMatrix.delete(copyBlockIdAndRootId);
      }
      let key = this.m_context.ToolsManager.getKey(copyData);
      if (copyData instanceof DbLine) {
        this.m_context.ModelManager.Lines[key].lines.push(copyData);
      } else if (copyData instanceof DbMesh) {
        this.m_context.ModelManager.Meshs[key].meshs.push(copyData);
      }
    }
    this.m_context.ModelManager.updateReferencesGeometry(this.m_copyDatas);
    let keyDatas = this.m_context.ToolsManager.filterRepeatKeyData(
      this.m_copyDatas
    );
    let { Lines, Meshs } = this.m_context.ModelManager;
    for (let i = 0; i < keyDatas.length; i++) {
      let key = keyDatas[i];
      if (Lines[key]) {
        this.m_context.ToolsManager.LinesToModel(
          Lines[key].lines,
          Lines[key].model,
        );
      }
      if (Meshs[key]) {
        this.m_context.ToolsManager.MeshToModel(
          Meshs[key].meshs,
          Meshs[key].model,
        );
      }
    }
    this.m_returnData.fullCopyDatas = this.m_copyDatas;
  }

  computerAngle(sourceAngle: number) {
    let angle = 0;
    let sourceRadius = THREE.MathUtils.degToRad(sourceAngle);
    let rotateAngle = this.m_curAngle * 2;

    if (sourceRadius >= 0 && sourceRadius < 90) {
      angle = THREE.MathUtils.degToRad(rotateAngle);
    } else {
      if (rotateAngle > 360) {
        rotateAngle = rotateAngle - 360;
      }
      angle = THREE.MathUtils.degToRad(360 - rotateAngle);
    }

    if (angle > Math.PI * 2) {
      angle = angle - Math.PI * 2;
    }

    return angle;
  }
  private clear() {
    this.m_context.ToolsManager.GroupClear(this.m_tempMoveObjectGroup);
    this.m_context.ToolsManager.ObjectsClear([
      this.m_locationObject,
      this.m_auxLine!,
    ]);
    this.m_dimension?.dispose();
  }
}

export { MirrorModify };
